import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { FilingCountry } from '../types/filingCountry'
import { AuthState } from '@/types/authState'
import { filingCountryConfigApi } from '../services/filingCountryConfigService.ts'
import type { CountryOption } from '@/types/deMinimisGroup'

export const useFilingCountryStore = defineStore('filingCountry', () => {
  // State for all filing countries
  const _filingCountries = ref<FilingCountry[]>([])
  const _isLoading = ref(false)
  const _error = ref<string | null>(null)

  // State for current country configuration (single country view)
  const _currentCountry = ref<FilingCountry | null>(null)
  const _configLoading = ref(false)
  const _lastUpdated = ref<Date | null>(null)

  const getFilingCountries = () => _filingCountries.value

  const setFilingCountries = (newFilingCountries: FilingCountry[]) => {
    _filingCountries.value = newFilingCountries
  }

  const updateFilingCountry = (filingCountry: FilingCountry) => {
    const index = _filingCountries.value.findIndex(
      item => item.CountryCode === filingCountry.CountryCode,
    )
    if (index !== -1) {
      _filingCountries.value[index] = filingCountry
    } else {
      _filingCountries.value.push(filingCountry)
    }

    // Also update currentCountry if it matches
    if (_currentCountry.value?.CountryCode === filingCountry.CountryCode) {
      _currentCountry.value = filingCountry
      _lastUpdated.value = new Date()
    }
  }

  const fetchFilingCountries = async (authState: AuthState) => {
    console.log('fetching filingCountries')
    _isLoading.value = true
    _error.value = null
    try {
      _filingCountries.value =
        await filingCountryConfigApi.getFilingCountries(authState)
    } catch (err) {
      _error.value =
        err instanceof Error ? err.message : 'Failed to load countries'
      throw err
    } finally {
      _isLoading.value = false
    }
  }

  // Current country actions (for configuration page)
  const setCurrentCountry = (country: FilingCountry | null) => {
    _currentCountry.value = country
  }

  const setConfigLoading = (loading: boolean) => {
    _configLoading.value = loading
  }

  /**
   * Updates template settings for a country
   */
  const updateTemplateSettings = async (
    authState: AuthState,
    countryCode: string,
    settings: {
      IncludedInPhysicalGoodsTemplate?: boolean
      IncludedInDigitalGoodsTemplate?: boolean
      IncludedInServicesTemplate?: boolean
    },
  ) => {
    _configLoading.value = true
    try {
      const updated = await filingCountryConfigApi.patchFilingCountryTemplate(
        authState,
        countryCode,
        settings,
      )
      updateFilingCountry(updated)
      return updated
    } finally {
      _configLoading.value = false
    }
  }

  // Computed properties for country options
  const countryOptions = computed<CountryOption[]>(() => {
    return _filingCountries.value.map(country => ({
      code: country.CountryCode,
      name: country.Name,
    }))
  })

  const isLoaded = computed(() => {
    return _filingCountries.value.length > 0
  })

  const isLoading = computed(() => _isLoading.value)
  const error = computed(() => _error.value)
  const currentCountry = computed(() => _currentCountry.value)
  const configLoading = computed(() => _configLoading.value)
  const lastUpdated = computed(() => _lastUpdated.value)

  return {
    // State
    filingCountries: _filingCountries,
    currentCountry,
    countryOptions,
    isLoading,
    configLoading,
    error,
    isLoaded,
    lastUpdated,

    // Actions
    getFilingCountries,
    setFilingCountries,
    updateFilingCountry,
    fetchFilingCountries,
    setCurrentCountry,
    setConfigLoading,
    updateTemplateSettings,
  }
})
